import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function toDisplaySeconds(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0.0';
  return numeric.toFixed(1);
}

function getShortcutArrow(from, to) {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const headSize = 8;
  const left = {
    x: to.x - headSize * Math.cos(angle - Math.PI / 6),
    y: to.y - headSize * Math.sin(angle - Math.PI / 6),
  };
  const right = {
    x: to.x - headSize * Math.cos(angle + Math.PI / 6),
    y: to.y - headSize * Math.sin(angle + Math.PI / 6),
  };
  return { left, right };
}

function getBoostVisual(boostType) {
  if (boostType === 'shield') {
    return {
      icon: '🛡',
      iconColor: '#bfdbfe',
      label: 'Shield',
    };
  }
  if (boostType === 'ghost') {
    return {
      icon: '👻',
      iconColor: '#ddd6fe',
      label: 'Ghost',
    };
  }
  return {
    icon: '⚡',
    iconColor: '#7dd3fc',
    label: 'Speed',
  };
}

export default function TagGame({ gameState, gamePlayers, user, socket, roomId }) {
  const { submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = normalizedState || gameState || {};

  const arena = state.arena || { width: 900, height: 560, walls: [], shortcuts: [] };
  const players = useMemo(() => state.players || [], [state.players]);
  const boosts = useMemo(() => state.boosts || [], [state.boosts]);
  const mySocketId = socket?.id || user?.socketId || null;
  const nowMs = Date.now();

  const [activeDirections, setActiveDirections] = useState({ up: false, down: false, left: false, right: false });

  const myPlayer = useMemo(
    () => players.find((player) => player.socketId === mySocketId || (user?.userId && player.userId === user.userId)),
    [players, mySocketId, user?.userId]
  );

  const itPlayer = useMemo(() => players.find((player) => player.isIt), [players]);
  const myBoostRemaining = Math.max(0, ((myPlayer?.boostUntil || 0) - nowMs) / 1000);
  const myShieldRemaining = Math.max(0, ((myPlayer?.shieldUntil || 0) - nowMs) / 1000);
  const myGhostRemaining = Math.max(0, ((myPlayer?.ghostUntil || 0) - nowMs) / 1000);

  const directionVector = useMemo(() => {
    const x = (activeDirections.right ? 1 : 0) - (activeDirections.left ? 1 : 0);
    const y = (activeDirections.down ? 1 : 0) - (activeDirections.up ? 1 : 0);
    if (x === 0 && y === 0) return { x: 0, y: 0 };
    const mag = Math.hypot(x, y);
    return { x: x / mag, y: y / mag };
  }, [activeDirections]);

  const prevDirectionRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    let cancelled = false;
    const sendMovement = async () => {
      if (!myPlayer || state.finished) return;
      if (
        prevDirectionRef.current.x === directionVector.x
        && prevDirectionRef.current.y === directionVector.y
      ) return;

      prevDirectionRef.current = directionVector;
      try {
        if (directionVector.x === 0 && directionVector.y === 0) {
          await submitAction({ type: 'stopMove' });
        } else {
          await submitAction({ type: 'setMove', x: directionVector.x, y: directionVector.y });
        }
      } catch (error) {
        if (!cancelled) {
          // noop, movement packets can fail safely
        }
      }
    };

    sendMovement();
    return () => {
      cancelled = true;
    };
  }, [directionVector, myPlayer, state.finished, submitAction]);

  useEffect(() => {
    const onBlur = () => {
      setActiveDirections({ up: false, down: false, left: false, right: false });
    };
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (state.finished) return;
      if (event.key === 'ArrowUp' || event.key.toLowerCase() === 'w') {
        event.preventDefault();
        setActiveDirections((current) => ({ ...current, up: true }));
      }
      if (event.key === 'ArrowDown' || event.key.toLowerCase() === 's') {
        event.preventDefault();
        setActiveDirections((current) => ({ ...current, down: true }));
      }
      if (event.key === 'ArrowLeft' || event.key.toLowerCase() === 'a') {
        event.preventDefault();
        setActiveDirections((current) => ({ ...current, left: true }));
      }
      if (event.key === 'ArrowRight' || event.key.toLowerCase() === 'd') {
        event.preventDefault();
        setActiveDirections((current) => ({ ...current, right: true }));
      }
    };

    const onKeyUp = (event) => {
      if (event.key === 'ArrowUp' || event.key.toLowerCase() === 'w') {
        event.preventDefault();
        setActiveDirections((current) => ({ ...current, up: false }));
      }
      if (event.key === 'ArrowDown' || event.key.toLowerCase() === 's') {
        event.preventDefault();
        setActiveDirections((current) => ({ ...current, down: false }));
      }
      if (event.key === 'ArrowLeft' || event.key.toLowerCase() === 'a') {
        event.preventDefault();
        setActiveDirections((current) => ({ ...current, left: false }));
      }
      if (event.key === 'ArrowRight' || event.key.toLowerCase() === 'd') {
        event.preventDefault();
        setActiveDirections((current) => ({ ...current, right: false }));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [state.finished]);

  const setDirectionPressed = (key, pressed) => {
    if (state.finished) return;
    setActiveDirections((current) => ({ ...current, [key]: pressed }));
  };

  const controlButton = (label, key, className = '') => {
    const isPressed = !!activeDirections[key];
    return (
      <button
        type="button"
        key={key}
        onPointerDown={(event) => {
          event.preventDefault();
          setDirectionPressed(key, true);
        }}
        onPointerUp={(event) => {
          event.preventDefault();
          setDirectionPressed(key, false);
        }}
        onPointerCancel={(event) => {
          event.preventDefault();
          setDirectionPressed(key, false);
        }}
        onPointerLeave={() => setDirectionPressed(key, false)}
        onContextMenu={(event) => event.preventDefault()}
        className={`h-14 w-14 select-none rounded-xl border text-xl font-bold transition active:scale-95 ${isPressed ? 'border-emerald-400 bg-emerald-500 text-white' : 'border-slate-400 bg-slate-100 text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'} ${className}`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="w-full p-3 text-slate-900 dark:text-slate-100">
      <div className="rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/95 p-4 shadow-sm dark:shadow-[0_30px_60px_rgba(2,6,23,0.45)]">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          
          <div className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
            Time Left: <span className="font-semibold text-emerald-600 dark:text-emerald-300">{Math.max(0, Number(state.remainingSeconds) || 0)}s</span>
          </div>
        </div>

        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-100 p-1 dark:border-slate-700 dark:bg-slate-950/80 sm:p-2">
          <svg
            viewBox={`0 0 ${arena.width} ${arena.height}`}
            className="block h-auto w-full rounded-xl bg-white dark:bg-slate-950"
            role="img"
            aria-label="Tag arena"
          >
            <defs>
              <pattern id="tagGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(148,163,184,0.3)" strokeWidth="1" />
              </pattern>
            </defs>

            <rect width={arena.width} height={arena.height} fill="url(#tagGrid)" />

            {(arena.walls || []).map((wall, index) => {
              const isDarkMode = document.documentElement.classList.contains('dark');
              return (
                <rect
                  key={`wall-${index}`}
                  x={wall.x}
                  y={wall.y}
                  width={wall.width}
                  height={wall.height}
                  rx="6"
                  fill={isDarkMode ? '#334155' : '#cbd5e1'}
                  stroke={isDarkMode ? '#64748b' : '#94a3b8'}
                  strokeWidth="2"
                />
              );
            })}

            {(arena.shortcuts || []).map((shortcut, index) => {
              const arrow = getShortcutArrow(shortcut.from, shortcut.to);
              const isDarkMode = document.documentElement.classList.contains('dark');
              return (
                <g key={`shortcut-${index}`}>
                  <circle
                    cx={shortcut.from.x}
                    cy={shortcut.from.y}
                    r={shortcut.from.radius}
                    fill={isDarkMode ? 'rgba(16,185,129,0.22)' : 'rgba(16,185,129,0.15)'}
                    stroke="#10b981"
                    strokeWidth="2"
                  />
                  <line
                    x1={shortcut.from.x}
                    y1={shortcut.from.y}
                    x2={shortcut.to.x}
                    y2={shortcut.to.y}
                    stroke={isDarkMode ? 'rgba(16,185,129,0.5)' : 'rgba(16,185,129,0.4)'}
                    strokeDasharray="6 6"
                    strokeWidth="2"
                  />
                  <circle cx={shortcut.to.x} cy={shortcut.to.y} r="6" fill="#10b981" />
                  <line x1={shortcut.to.x} y1={shortcut.to.y} x2={arrow.left.x} y2={arrow.left.y} stroke="#10b981" strokeWidth="2" />
                  <line x1={shortcut.to.x} y1={shortcut.to.y} x2={arrow.right.x} y2={arrow.right.y} stroke="#10b981" strokeWidth="2" />
                </g>
              );
            })}

            {boosts.map((boost) => {
              const isReady = nowMs >= (boost.readyAt || 0);
              const visual = getBoostVisual(boost.type);
              const isDarkMode = document.documentElement.classList.contains('dark');
              return (
                <g key={boost.id || `${boost.x}-${boost.y}`} opacity={isReady ? 1 : 0.28}>
                  <text
                    x={boost.x}
                    y={boost.y + 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="30"
                    fontWeight="900"
                    fill={visual.iconColor}
                    stroke={isDarkMode ? '#0f172a' : '#f8fafc'}
                    strokeWidth="1.4"
                    paintOrder="stroke"
                  >
                    {visual.icon}
                  </text>
                </g>
              );
            })}

            {players.map((player) => {
              const isMe = !!myPlayer && myPlayer.socketId === player.socketId;
              const isBoosted = nowMs < (player.boostUntil || 0);
              const isShielded = nowMs < (player.shieldUntil || 0);
              const isGhosted = nowMs < (player.ghostUntil || 0);
              const isDarkMode = document.documentElement.classList.contains('dark');
              return (
                <g key={player.socketId}>
                  <circle
                    cx={player.x}
                    cy={player.y}
                    r={state.pieceRadius || 14}
                    fill={player.color || '#94a3b8'}
                    stroke={player.isIt ? '#f43f5e' : isMe ? '#ffffff' : (isDarkMode ? '#0f172a' : '#f8fafc')}
                    strokeWidth={player.isIt ? 4 : 2.5}
                  />
                  {isBoosted ? (
                    <circle
                      cx={player.x}
                      cy={player.y}
                      r={(state.pieceRadius || 14) + 11}
                      fill="none"
                      stroke="#22d3ee"
                      strokeWidth="2"
                      strokeDasharray="4 4"
                    />
                  ) : null}
                  {isShielded ? (
                    <circle
                      cx={player.x}
                      cy={player.y}
                      r={(state.pieceRadius || 14) + 15}
                      fill="none"
                      stroke="#60a5fa"
                      strokeWidth="2"
                      strokeDasharray="2 5"
                    />
                  ) : null}
                  {isGhosted ? (
                    <circle
                      cx={player.x}
                      cy={player.y}
                      r={(state.pieceRadius || 14) + 18}
                      fill="none"
                      stroke="#a78bfa"
                      strokeWidth="2"
                      opacity="0.8"
                      strokeDasharray="8 6"
                    />
                  ) : null}
                  {player.isIt ? (
                    <circle
                      cx={player.x}
                      cy={player.y}
                      r={(state.pieceRadius || 14) + 7}
                      fill="none"
                      stroke="#fb7185"
                      strokeWidth="2"
                      strokeDasharray="5 5"
                    />
                  ) : null}
                  <text
                    x={player.x}
                    y={player.y - (state.pieceRadius || 14) - 12}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="11"
                    fontWeight="600"
                    fill="#e2e8f0"
                    pointerEvents="none"
                  >
                    {player.username}
                  </text>
                  <text
                    x={player.x}
                    y={player.y + 1}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="16"
                    fontWeight="700"
                    fill="#f8fafc"
                  >
                    {(player.icon || '?').toString().slice(0, 1).toUpperCase()}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-3 text-center text-sm font-semibold text-slate-900 dark:text-slate-100">Controller</div>
          <p className="mb-3 text-center text-xs text-slate-600 dark:text-slate-400">Desktop: use WASD or the arrows</p>
          <div className="mx-auto flex w-[190px] flex-col gap-2">
            <div className="grid grid-cols-3 gap-2">
              <div />
              {controlButton('↑', 'up')}
              <div />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {controlButton('←', 'left')}
              {controlButton('↓', 'down')}
              {controlButton('→', 'right')}
            </div>
          </div>
        </div>

        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {players
            .slice()
            .sort((a, b) => a.itTimeSeconds - b.itTimeSeconds)
            .map((player) => (
              <div
                key={`score-${player.socketId}`}
                className={`rounded-xl border px-3 py-2 text-sm ${player.isIt ? 'border-rose-300 bg-rose-50 dark:border-rose-400/80 dark:bg-rose-900/30' : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/70'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-900 dark:text-slate-100">{player.username}</span>
                  <span className="text-slate-600 dark:text-slate-300">{toDisplaySeconds(player.itTimeSeconds)}s It</span>
                </div>
                {nowMs < (player.boostUntil || 0) ? (
                  <div className="mt-1 text-xs font-semibold text-sky-600 dark:text-sky-300">Boosted</div>
                ) : null}
                {nowMs < (player.shieldUntil || 0) ? (
                  <div className="mt-1 text-xs font-semibold text-blue-600 dark:text-blue-300">Shielded</div>
                ) : null}
                {nowMs < (player.ghostUntil || 0) ? (
                  <div className="mt-1 text-xs font-semibold text-violet-600 dark:text-violet-300">Ghosted</div>
                ) : null}
              </div>
            ))}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
          <div className="font-semibold text-slate-900 dark:text-slate-100">Status</div>
          {itPlayer ? (
            <p className="mt-1">{itPlayer.username} is currently <span className="font-bold text-rose-600 dark:text-rose-300">It</span>.</p>
          ) : (
            <p className="mt-1">Waiting for players...</p>
          )}
          <p className="mt-1">Arena: <span className="text-slate-900 dark:text-slate-100">{arena.name || 'Custom Arena'}</span></p>
          <p className="mt-1 text-slate-600 dark:text-slate-300">Power-ups on map: <span className="text-sky-600 dark:text-sky-300">{boosts.length}</span></p>
          {myBoostRemaining > 0 ? (
            <p className="mt-2 rounded-lg bg-sky-100 px-2 py-1 text-sky-800 dark:bg-sky-950/50 dark:text-sky-200">Speed boost active: {myBoostRemaining.toFixed(1)}s</p>
          ) : null}
          {myShieldRemaining > 0 ? (
            <p className="mt-2 rounded-lg bg-blue-100 px-2 py-1 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200">Shield active: {myShieldRemaining.toFixed(1)}s</p>
          ) : null}
          {myGhostRemaining > 0 ? (
            <p className="mt-2 rounded-lg bg-violet-100 px-2 py-1 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200">Ghost active: {myGhostRemaining.toFixed(1)}s</p>
          ) : null}
          {state.finished ? (
            <p className="mt-2 rounded-lg bg-emerald-100 px-2 py-1 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">Game finished. Lowest It time wins.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

TagGame.Guide = function TagGuide() {
  return (
    <div className="text-sm text-slate-800 dark:text-slate-200">
      Everyone moves at once. If you are It, chase and touch another player to pass It on. Shortcuts help reposition quickly, and power-ups rotate effects: speed (⚡), shield (🛡), and ghost (👻) for temporary wall phasing. The winner is the player with the lowest total time as It when the timer ends.
    </div>
  );
};
